import React, { lazy, Suspense } from "react";
import { Route, Routes } from "react-router-dom";
import MainLayout from "../../components/Layout/MainLayout";
import NotFound from "../../pages/NotFound";

// Lazy load components for better performance
const Dashboard = lazy(() => import("../../pages/Dashboard"));
const NewEmployeeList = lazy(() => import("../../pages/Employee/EmployeeList"));
const NewInactiveEmployees = lazy(() =>
  import("../../pages/Employee/InactiveEmployees")
);
const NewCustomerManagement = lazy(() =>
  import("../../pages/Customer/CustomerManagement")
);
const NewInactiveCustomer = lazy(() =>
  import("../../pages/Customer/InactiveCustomer")
);
const NewItemManagement = lazy(() =>
  import("../../pages/Items/ItemsManagement")
);
const NewAMCManagement = lazy(() => import("../../pages/AMC/AMCManagement"));
const NewQuotations = lazy(() => import("../../pages/Quotations"));
const NewUpcomingSurveys = lazy(() =>
  import("../../pages/Surveys/UpcomingSurveys")
);
const NewCompletedSurveys = lazy(() =>
  import("../../pages/Surveys/CompletedSurvey")
);
const NewPurchaseOrders = lazy(() => import("../../pages/PurchaseOrders"));
const NewLeads = lazy(() => import("../../pages/lead"));

const NewLicenseCopy = lazy(() => import("../../pages/LicenseCopy/index"));
const Settings = lazy(() => import("../../pages/Settings"));
const GeneralSettings = lazy(() => import("../features/settings/General"));
const EmployeeSettings = lazy(() => import("../features/settings/Employee"));
const DocumentationSettings = lazy(() =>
  import("../features/settings/Documentation")
);
const SystemsSettings = lazy(() => import("../features/settings/Systems"));
const BrochureDemo = lazy(() => import("../../pages/Brochures/BrochureDemo"));
const BrochureViewer = lazy(() =>
  import("../../pages/Brochures/BrochureViewer")
);
const CompanyOverviewBrochure = lazy(() =>
  import("../../pages/Brochures/CompanyOverviewBrochure")
);
const ServicesBrochure = lazy(() =>
  import("../../pages/Brochures/ServicesBrochure")
);
const ContactBrochure = lazy(() =>
  import("../../pages/Brochures/ContactBrochure")
);


// Reusable route wrapper compo nent
const RouteWrapper = ({ active, children }) => (
  <MainLayout active={active}>
    <Suspense fallback={<div>Loading...</div>}>{children}</Suspense>
  </MainLayout>
);

const dashboardRoutes = [{ path: "/dashboard", component: Dashboard }];

const newEmployeeRoutes = [
  { path: "/employees/*", component: NewEmployeeList },
  {
    path: "/employees/create",
    component: lazy(() =>
      import("../../pages/Employee/components/Employee.manage")
    ),
  },
  {
    path: "/employees/:id/edit",
    component: lazy(() =>
      import("../../pages/Employee/components/Employee.manage")
    ),
    props: { isEdit: true },
  },
  {
    path: "/employees/:id",
    component: lazy(() =>
      import("../../pages/Employee/components/Employee.view")
    ),
  },
  {
    path: "/employees/inactive/:id",
    component: lazy(() =>
      import("../../pages/Employee/components/Employee.view")
    ),
  },
  {
    path: "/employees/salary",
    component: lazy(() =>
      import("../../pages/Employee/components/Employee.salary")
    ),
  },
  {
    path: "/employees/attendance",
    component: lazy(() =>
      import("../../pages/Employee/components/Employee.attendance")
    ),
  },
  {
    path: "/employees/attendance/accept",
    component: lazy(() =>
      import("../../pages/Employee/components/Employee.acceptAttendance")
    ),
  },
  { path: "/employees/inactive/*", component: NewInactiveEmployees },
  {
    path: "/employees/:employeeId/offerLetter",
    component: lazy(() =>
      import("../../pages/Employee/components/Employee.offerLetter")
    ),
  },
];

const newCustomerRoutes = [
  { path: "/customers/*", component: NewCustomerManagement },
  {
    path: "/customers/create",
    component: lazy(() =>
      import("../../pages/Customer/components/Customer.manage")
    ),
  },
  {
    path: "/customers/:id/edit",
    component: lazy(() =>
      import("../../pages/Customer/components/Customer.manage")
    ),
    props: { isEdit: true },
  },
  {
    path: "/customers/:id",
    component: lazy(() =>
      import("../../pages/Customer/components/Customer.view")
    ),
  },
  {
    path: "/customers/inactive/:id",
    component: lazy(() =>
      import("../../pages/Customer/components/Customer.view")
    ),
  },
  { path: "/customers/inactive/*", component: NewInactiveCustomer },
];

const newItemRoutes = [
  {
    path: "/items/create",
    component: lazy(() => import("../../pages/Items/components/Items.manage")),
  },
  {
    path: "/items/:id/edit",
    component: lazy(() => import("../../pages/Items/components/Items.manage")),
    props: { isEdit: true },
  },
  {
    path: "/items/:id",
    component: lazy(() => import("../../pages/Items/components/Items.view")),
  },
  { path: "/items/*", component: NewItemManagement },
];

const newAMCRoutes = [
  {
    path: "/amcs/:id/export",
    component: lazy(() => import("../../pages/AMC/components/AMC.export")),
  },
  {
    path: "/amcs/:id/edit",
    component: lazy(() => import("../../pages/AMC/components/AMC.manage")),
    props: { isEdit: true },
  },
  {
    path: "/amcs/create",
    component: lazy(() => import("../../pages/AMC/components/AMC.manage")),
  },
  {
    path: "/amcs/:id",
    component: lazy(() => import("../../pages/AMC/components/AMC.view")),
  },
  { path: "/amcs/*", component: NewAMCManagement },
];

const newQuotationRoutes = [
  {
    path: "/quotations/manage",
    component: lazy(() =>
      import("../../pages/Quotations/components/Quotations.manage")
    ),
  },
  {
    path: "/quotations/:id/edit",
    component: lazy(() =>
      import("../../pages/Quotations/components/Quotations.manage")
    ),
    props: { isEdit: true },
  },
  {
    path: "/quotations/:id",
    component: lazy(() =>
      import("../../pages/Quotations/components/Quotations.view")
    ),
  },
  { path: "/quotations/*", component: NewQuotations },
];

const surveyRoutes = [
  { path: "/surveys", component: NewUpcomingSurveys },
  {
    path: "/surveys/completed",
    component: NewCompletedSurveys,
    props: { conducted: true },
  },
  {
    path: "/surveys/:surveyId",
    component: lazy(() =>
      import("../../pages/Surveys/components/Surveys.view")
    ),
  },
  {
    path: "/surveys/create",
    component: lazy(() =>
      import("../../pages/Surveys/components/Surveys.manage")
    ),
    props: { isEdit: false },
  },
  {
    path: "/surveys/manage/:surveyId",
    component: lazy(() =>
      import("../../pages/Surveys/components/Surveys.manage")
    ),
    props: { isEdit: true },
  },
];

const visitRoutes = [
  {
    path: "/visits",
    component: lazy(() => import("../../pages/AMC/components/AMC.visits")),
  },
  {
    path: "/visits/:id",
    component: lazy(() => import("../../pages/Visits/components/Visits.view")),
  },
  {
    path: "/visits/manage",
    component: lazy(() =>
      import("../../pages/Visits/components/Visits.manage")
    ),
  },
  {
    path: "/visits/manage/:id",
    component: lazy(() =>
      import("../../pages/Visits/components/Visits.manage")
    ),
    props: { isEdit: true },
  },
];

const reportRoutes = [
  {
    path: "/reports/formA",
    component: lazy(() =>
      import("../../pages/Reports/components/Reports.formAList")
    ),
  },
  {
    path: "/generatereport/formA",
    component: lazy(() =>
      import("../../pages/Reports/components/Reports.formA")
    ),
  },
  {
    path: "/view/formA/:id",
    component: lazy(() =>
      import("../../pages/Reports/components/Reports.formA")
    ),
    props: { view: true },
  },
  {
    path: "/edit/formA/:id",
    component: lazy(() =>
      import("../../pages/Reports/components/Reports.formA")
    ),
    props: { edit: true },
  },
  {
    path: "/reports/formB",
    component: lazy(() =>
      import("../../pages/Reports/components/Reports.formBList")
    ),
  },
  {
    path: "/generatereport/formB",
    component: lazy(() =>
      import("../../pages/Reports/components/Reports.formB")
    ),
  },
  {
    path: "/view/formB/:id",
    component: lazy(() =>
      import("../../pages/Reports/components/Reports.formB")
    ),
    props: { view: true },
  },
  {
    path: "/edit/formB/:id",
    component: lazy(() =>
      import("../../pages/Reports/components/Reports.formB")
    ),
    props: { edit: true },
  },
  {
    path: "/reports/trainingCertificate",
    component: lazy(() =>
      import("../../pages/Reports/components/Reports.trainingList")
    ),
  },
  {
    path: "/generatereport/trainingCertificate",
    component: lazy(() =>
      import("../../pages/Reports/components/Reports.trainingCertificate")
    ),
  },
  {
    path: "/view/trainingCertificate/:id",
    component: lazy(() =>
      import("../../pages/Reports/components/Reports.trainingCertificate")
    ),
    props: { view: true },
  },
  {
    path: "/edit/trainingCertificate/:id",
    component: lazy(() =>
      import("../../pages/Reports/components/Reports.trainingCertificate")
    ),
    props: { edit: true },
  },
  {
    path: "/reports/installationCertificate",
    component: lazy(() =>
      import("../../pages/Reports/components/Reports.installationList")
    ),
  },
  {
    path: "/generatereport/installationCertificate",
    component: lazy(() =>
      import("../../pages/Reports/components/Reports.installationCertificate")
    ),
  },
  {
    path: "/view/installationCertificate/:id",
    component: lazy(() =>
      import("../../pages/Reports/components/Reports.installationCertificate")
    ),
    props: { view: true },
  },
  {
    path: "/edit/installationCertificate/:id",
    component: lazy(() =>
      import("../../pages/Reports/components/Reports.installationCertificate")
    ),
    props: { edit: true },
  },
  {
    path: "/reports/testCertificate",
    component: lazy(() =>
      import("../../pages/Reports/components/Reports.testingList")
    ),
  },
  {
    path: "/generatereport/testingCertificate",
    component: lazy(() =>
      import("../../pages/Reports/components/Reports.testingCertificate")
    ),
  },
  {
    path: "/view/testingCertificate/:id",
    component: lazy(() =>
      import("../../pages/Reports/components/Reports.testingCertificate")
    ),
    props: { view: true },
  },
  {
    path: "/edit/testingCertificate/:id",
    component: lazy(() =>
      import("../../pages/Reports/components/Reports.testingCertificate")
    ),
    props: { edit: true },
  },
];

const newPurchaseOrders = [
  {
    path: "/purchaseOrders/create",
    component: lazy(() =>
      import("../../pages/PurchaseOrders/components/PurchaseOrders.manage")
    ),
  },
  {
    path: "/purchaseOrders/:id/edit",
    component: lazy(() =>
      import("../../pages/PurchaseOrders/components/PurchaseOrders.manage")
    ),
    props: { isEdit: true },
  },
  {
    path: "/purchaseOrders/:id",
    component: lazy(() =>
      import("../../pages/PurchaseOrders/components/PurchaseOrders.view")
    ),
  },
  { path: "/purchaseOrders/*", component: NewPurchaseOrders },
];

const boqRoutes = [
  { path: "/boq", component: lazy(() => import("../../pages/BOQ")) },
  {
    path: "/boq/details/:boqId",
    component: lazy(() => import("../../pages/BOQ/components/BOQ.view")),
  },
  {
    path: "/boq/manage",
    component: lazy(() => import("../../pages/BOQ/components/BOQ.manage")),
  },
  {
    path: "/boq/manage/:boqId",
    component: lazy(() => import("../../pages/BOQ/components/BOQ.manage")),
    props: { isEdit: true },
  },
  { path: "/boqItems", component: lazy(() => import("../../pages/BOQItems")) },
  {
    path: "/boqItems/importPreview",
    component: lazy(() =>
      import("../../pages/BOQItems/components/BOQItems.importPreview")
    ),
  },
  {
    path: "/boqItems/details/:boqItemId",
    component: lazy(() =>
      import("../../pages/BOQItems/components/BOQItems.view")
    ),
  },
  {
    path: "/boqItems/manage",
    component: lazy(() =>
      import("../../pages/BOQItems/components/BOQItems.manage")
    ),
  },
  {
    path: "/boqItems/manage/:id",
    component: lazy(() =>
      import("../../pages/BOQItems/components/BOQItems.manage")
    ),
    props: { isEdit: true },
  },
];

const newLeadRoutes = [
  {
    path: "/leads/create",
    component: lazy(() => import("../../pages/Lead/components/Lead.manage")),
  },
  {
    path: "/leads/:id/edit",
    component: lazy(() => import("../../pages/Lead/components/Lead.manage")),
    props: { isEdit: true },
  },
  {
    path: "/leads/:id",
    component: lazy(() => import("../../pages/Lead/components/Lead.view")),
  },
  { path: "/leads/*", component: NewLeads },
];

const taskRoutes = [
  { path: "/task", component: lazy(() => import("../../pages/Task")) },
  {
    path: "/task/details/:taskId",
    component: lazy(() => import("../../pages/Task/components/Task.view")),
  },
  {
    path: "/task/manage",
    component: lazy(() => import("../../pages/Task/components/Task.manage")),
  },
  {
    path: "/task/manage/:id",
    component: lazy(() => import("../../pages/Task/components/Task.manage")),
    props: { isEdit: true },
  },
];

const newCashLedgerRoutes = [
  {/** Sell Cash Ledger */},
  { path: "/cashLedger/sell/create", component: lazy(() => import("../../pages/CashLedgerNew/components/CashLedgerNew.manage")) },
  { path: "/cashLedger/sell/:id/edit", component: lazy(() => import("../../pages/CashLedgerNew/components/CashLedgerNew.manage")), props: { isEdit: true } },
  { path: "/cashLedger/sell/:id", component: lazy(() => import("../../pages/CashLedgerNew/components/CashLedgerNew.view")) },
  { path: "/cashLedger/sell", component: lazy(() => import("../../pages/CashLedgerNew/SellCashLedger.module")) },
  {/** Purchase Cash Ledger */},
  { path: "/cashLedger/purchase/create", component: lazy(() => import("../../pages/CashLedgerNew/components/CashLedgerNew.manage")) },
  { path: "/cashLedger/purchase/:id/edit", component: lazy(() => import("../../pages/CashLedgerNew/components/CashLedgerNew.manage")), props: { isEdit: true } },
  { path: "/cashLedger/purchase/:id", component: lazy(() => import("../../pages/CashLedgerNew/components/CashLedgerNew.view")) },
  { path: "/cashLedger/purchase", component: lazy(() => import("../../pages/CashLedgerNew/PurchaseCashLedger.module")) },
];

const newlicenseCopyRoutes = [
  {
    path: "/licenseCopies/create",
    component: lazy(() =>
      import("../../pages/LicenseCopy/components/LicenseCopy.manage")
    ),
  },
  {
    path: "/licenseCopies/:id/edit",
    component: lazy(() =>
      import("../../pages/LicenseCopy/components/LicenseCopy.manage")
    ),
    props: { isEdit: true },
  },
  {
    path: "/licenseCopies/:id",
    component: lazy(() =>
      import("../../pages/LicenseCopy/components/LicenseCopy.view")
    ),
  },
  { path: "/licenseCopies/*", component: NewLicenseCopy },
];

const settingsRoutes = [
  { path: "/settings", component: Settings },
  { path: "/settings/general", component: GeneralSettings },
  { path: "/settings/employee", component: EmployeeSettings },
  { path: "/settings/documentation", component: DocumentationSettings },
  { path: "/settings/systems", component: SystemsSettings },
];

const brochureRoutes = [
  { path: "/brochures", component: BrochureDemo },
  { path: "/brochures/viewer", component: BrochureViewer },
  { path: "/brochures/company-overview", component: CompanyOverviewBrochure },
  { path: "/brochures/services", component: ServicesBrochure },
  { path: "/brochures/contact", component: ContactBrochure },
];

// Helper function to create routes
const createRoutes = (routes, active) => {
  return routes.map(({ path, component: Component, props = {} }) => (
    <Route
      key={path}
      path={path}
      element={
        <RouteWrapper active={active}>
          <Component {...props} />
        </RouteWrapper>
      }
    />
  ));
};

const SuperAdminRoutes = () => {
  return (
    <Routes>
      {/* ===== Discarded Routes ===== */}
      {/* {createRoutes(employeeRoutes, "employees")} */}
      {/* {createRoutes(customerRoutes, "customers")} */}
      {/* {createRoutes(itemRoutes, "items")} */}
      {/* {createRoutes(quotationRoutes, "quotations")} */}
      {/* {createRoutes(amcRoutes, "amcs")} */}
      {/* {createRoutes(purchaseOrderRoutes, "purchaseOrder")} */}
      {/* {createRoutes(leadRoutes, "lead")} */}
      {/* {createRoutes(licenseCopyRoutes, "licenseCopy")}
      {createRoutes(cashLedgerRoutes, "cashLedger")} */}
      {/* {createRoutes(newLeadRoutes, "leads")} */}

      {/* ===== Final routes ===== */}
      {createRoutes(dashboardRoutes, "dashboard")}
      {createRoutes(newEmployeeRoutes, "employees")}
      {createRoutes(newCustomerRoutes, "customers")}
      {createRoutes(newItemRoutes, "items")}
      {createRoutes(newAMCRoutes, "amcs")}
      {createRoutes(newQuotationRoutes, "quotations")}
      {createRoutes(surveyRoutes, "surveys")}
      {createRoutes(visitRoutes, "visits")}
      {createRoutes(reportRoutes, "reports")}
      {createRoutes(newPurchaseOrders, "purchaseOrders")}
      {createRoutes(boqRoutes, "boq")}
      {createRoutes(newLeadRoutes, "leads")}
      {createRoutes(taskRoutes, "task")}
      {createRoutes(newCashLedgerRoutes, "cashLedger")}
      {createRoutes(newlicenseCopyRoutes, "licenseCopies")}
      {createRoutes(settingsRoutes, "settings")}
      {createRoutes(brochureRoutes, "brochures")}

      {/* ===== Not Found ===== */}
      <Route path="/:notfound" element={<NotFound />} />
    </Routes>
  );
};

export default SuperAdminRoutes;

// ===== Discarded Routes =====
//
// const employeeRoutes = [
//   { path: "/employees", component: NewEmployeeList },
//   {
//     path: "/employees/inactive",
//     component: NewInactiveEmployees,
//   },
//   { path: "/employees/attendance", component: lazy(() => import("../features/employee/EmployeeAttendance")) },
//   { path: "/employees/attendance/accept", component: lazy(() => import("../features/employee/AcceptAttendance")) },
//   {
//     path: "/employees/manage",
//     component: lazy(() => import("../features/employee/ManageEmployees")),
//   },
//   {
//     path: "/employees/manage/:employeeId",
//     component: lazy(() => import("../features/employee/ManageEmployees")),
//     props: { isEdit: true },
//   },
//   {
//     path: "/employees/:employeeId",
//     component: lazy(() => import("../features/employee/EmployeeDetails")),
//   },
//   {
//     path: "/employees/salary",
//     component: lazy(() => import("../features/employee/Salary")),
//   },
// ];

// const customerRoutes = [
//   { path: "/customers", component: NewCustomerManagement },
//   {
//     path: "/customers/inactive",
//     component: NewInactiveCustomer,
//   },
//   {
//     path: "/customers/manage",
//     component: lazy(() => import("../features/customers/ManageCustomers")),
//   },
//   {
//     path: "/customers/manage/:customerId",
//     component: lazy(() => import("../features/customers/ManageCustomers")),
//     props: { isEdit: true },
//   },
//   {
//     path: "/customers/:customerId",
//     component: lazy(() => import("../features/customers/CustomerDetails")),
//   },
// ];

// const itemRoutes = [
//   { path: "/items", component: NewItemManagement },
//   {
//     path: "/items/:id",
//     component: lazy(() => import("../features/items/ItemDetails")),
//   },
//   {
//     path: "/items/manage",
//     component: lazy(() => import("../features/items/ManageItems")),
//   },
//   {
//     path: "/items/manage/:id",
//     component: lazy(() => import("../features/items/ManageItems")),
//     props: { isEdit: true },
//   },
// ];

// const quotationRoutes = [
//   { path: "/quotations", component: NewQuotations },
//   {
//     path: "/quotations/:quotationId",
//     component: lazy(() => import("../features/quotations/QuoatationDetails")),
//   },
//   {
//     path: "/quotations/manage",
//     component: lazy(() => import("../features/quotations/ManageQuoatation")),
//   },
//   {
//     path: "/quotations/manage/:id",
//     component: lazy(() => import("../features/quotations/ManageQuoatation")),
//     props: { isEdit: true },
//   },
// ];

// const amcRoutes = [
//   { path: "/amcs", component: NewAMCManagement },
//   {
//     path: "/amcs/details/:amcId",
//     component: lazy(() => import("../features/amc/AmcDetails")),
//   },
//   {
//     path: "/amcs/:amcId",
//     component: lazy(() => import("../features/amc/AmcExport")),
//   },
//   {
//     path: "/amcs/manage",
//     component: lazy(() => import("../features/amc/ManageAMCs")),
//   },
//   {
//     path: "/amcs/manage/:id",
//     component: lazy(() => import("../features/amc/ManageAMCs")),
//     props: { isEdit: true },
//   },
// ];

// const leadRoutes = [
//   { path: "/lead", component: NewLeads },
//   {
//     path: "/lead/details/:leadId",
//     component: lazy(() => import("../features/lead/LeadDetails")),
//   },
//   {
//     path: "/lead/manage",
//     component: lazy(() => import("../features/lead/ManageLead")),
//   },
//   {
//     path: "/lead/manage/:id",
//     component: lazy(() => import("../features/lead/ManageLead")),
//     props: { isEdit: true },
//   },
// ];

// const licenseCopyRoutes = [
//   {
//     path: "/licenseCopy",
//     component: NewLicenseCopy,
//   },
//   {
//     path: "/licenseCopy/details/:licenseCopyId",
//     component: lazy(() => import("../features/licenseCopy/licenseCopyDetails")),
//   },
//   {
//     path: "/licenseCopy/manage",
//     component: lazy(() => import("../features/licenseCopy/ManageLicenseCopy")),
//   },
//   {
//     path: "/licenseCopy/manage/:id",
//     component: lazy(() => import("../features/licenseCopy/ManageLicenseCopy")),
//     props: { isEdit: true },
//   },
// ];

// const cashLedgerRoutes = [
//   {
//     path: "/cashLedger",
//     component: lazy(() => import("../pages/CashLedger")),
//     props: { defaultType: "Sell" }
//   },
//   {
//     path: "/cashLedger/sell",
//     component: newSellCashLedger
//   },
//   {
//     path: "/cashLedger/purchase",
//     component: newPurchaseCashLedger
//   },
//   {
//     path: "/cashLedger/details/:cashLedgerId",
//     component: lazy(() => import("../features/cashLedger/cashLedgerDetails")),
//   },
//   {
//     path: "/cashLedger/manage",
//     component: lazy(() => import("../features/cashLedger/ManageCashLedger")),
//   },
//   {
//     path: "/cashLedger/manage/:id",
//     component: lazy(() => import("../features/cashLedger/ManageCashLedger")),
//     props: { isEdit: true },
//   },
// ];

// const newSurveyRoutes = [
//   { path: "/new-survey/*", component: NewSurveys },
// ];
